import { Icon, IconType } from "./icon/Icon";

import { sendEvent } from "@/shared/utils/analytics/gtag";

interface ModeStyle {
  block: string;
  icon: string;
}

type ButtonMode = "filled" | "bezeled" | "gray" | "plain" | "event";

interface ButtonProps {
  onClick?: () => void;
  size?: "tiny" | "small" | "default" | "large";
  mode?: ButtonMode;
  text?: string;
  icon?: IconType;
  loading?: boolean;
  pilled?: boolean;
  disabled?: boolean;
  className?: string;
  analyticsEvent?: {
    event: string;
    category?: string;
    label?: string;
  };
}

export const Button: React.FC<ButtonProps> = ({
  onClick,
  size = "default",
  mode = "filled",
  text,
  icon,
  loading = false,
  pilled = false,
  disabled = false,
  className,
  analyticsEvent,
}) => {
  const sizeClasses: Record<string, ModeStyle> = {
    tiny: {
      block: "px-2 py-1 text-[0.9375rem] leading-[1.25rem]",
      icon: "w-6 h-6",
    },
    small: {
      block: "px-3 py-2 text-[0.9375rem] leading-[1.25rem]",
      icon: "w-6 h-6",
    },
    default: {
      block: "px-3.5 py-3 text-[0.9375rem] leading-[1.375rem]",
      icon: "w-6 h-6",
    },
    large: {
      block: "px-4 py-3 text-[1.0625rem] leading-[1.625rem]",
      icon: "w-6 h-6",
    },
  };

  const modeClasses: Record<ButtonMode, ModeStyle> = {
    filled: {
      block: "bg-button text-button-text",
      icon: "fill-white",
    },
    bezeled: {
      block: "bg-secondary-fill text-link",
      icon: "fill-button",
    },
    gray: {
      block: "bg-plain-background text-plain-foreground",
      icon: "fill-plain-foreground",
    },
    plain: {
      block: "text-link",
      icon: "fill-link",
    },
    event: {
      block: "bg-surface-dark text-white",
      icon: "fill-white",
    },
  };

  const buttonClass = [
    "flex items-center justify-center gap-2 font-semibold",
    sizeClasses[size].block,
    pilled ? "rounded-full" : "rounded-[0.75rem]",
    disabled && "opacity-35 pointer-events-none",
    loading && "py-[0.8125rem]",
    className,
    modeClasses[mode].block,
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = () => {
    if (analyticsEvent) {
      const { event, category, label } = analyticsEvent;
      sendEvent(event, {
        event_category: category,
        event_label: label,
      });
    }
    onClick?.();
  };

  return (
    <button onClick={handleClick} disabled={disabled} className={buttonClass}>
      {!loading && icon && (
        <Icon
          type={icon}
          className={`${modeClasses[mode].icon} ${sizeClasses[size].icon}`}
        />
      )}
      {!loading && text && <p>{text}</p>}

      {loading && (
        <Icon
          type="loading"
          className={`${modeClasses[mode].icon} ${sizeClasses[size].icon} animate-spin`}
        />
      )}
    </button>
  );
};
